/**
 * One confirmation dialog for the whole application, behind a promise.
 *
 * Ten `window.confirm` calls sat behind an eslint disable naming issue #108.
 * A native confirm cannot be styled, cannot be reached by the E2E suite, blocks
 * the main thread, and names the action in the browser's voice rather than the
 * product's.
 *
 * `useConfirm` returns a promise so a caller reads as a straight line rather
 * than a callback and a piece of dialog state per call site.
 *
 * @module components/ui/ConfirmProvider
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "./Button";
import { Modal } from "./Modal";

export interface ConfirmOptions {
  title: string;
  body?: React.ReactNode;
  /** A verb and its object: "Delete module", never "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` recolours the confirming button for a destructive action. */
  tone?: "default" | "danger";
  "data-testid"?: string;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Ask for confirmation and wait for the answer.
 *
 * @returns A function resolving `true` when the person confirmed.
 * @throws When called outside {@link ConfirmProvider}.
 *
 * @example
 * ```tsx
 * const confirm = useConfirm();
 * if (await confirm({ title: "Remove module?", confirmLabel: "Remove module", tone: "danger" })) {
 *   removeModule(id);
 * }
 * ```
 */
export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm requires a ConfirmProvider above it in the tree.");
  }
  return confirm;
}

/**
 * `useConfirm` for a component that only sometimes asks, so a screen that
 * renders it outside the provider still works until the moment it must ask.
 */
export function useOptionalConfirm(): Confirm | null {
  return useContext(ConfirmContext);
}

interface PendingConfirm {
  options: ConfirmOptions;
  settle: (confirmed: boolean) => void;
}

/** Mount once, at the shell root. */
export function ConfirmProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // Held so a close path that fires after the state has cleared still settles
  // the promise; an unsettled one would hang the caller for ever.
  const settleRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A question raised while another is open supersedes it. The one being
        // replaced still has a caller awaiting an answer, so it settles as a
        // refusal; left unsettled it would hang that caller for ever.
        settleRef.current?.(false);
        settleRef.current = resolve;
        setPending({ options, settle: resolve });
      }),
    [],
  );

  // Unmounting with a question open is the third way out, after answering and
  // being superseded. Its caller is still awaiting, so it refuses like the
  // others rather than hanging.
  useEffect(
    () => () => {
      settleRef.current?.(false);
      settleRef.current = null;
    },
    [],
  );

  const close = useCallback((confirmed: boolean) => {
    settleRef.current?.(confirmed);
    settleRef.current = null;
    setPending(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);
  const testId = pending?.options["data-testid"] ?? "confirm-dialog";

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && (
        <Modal
          open
          onClose={() => {
            close(false);
          }}
          title={pending.options.title}
          size="sm"
          tone={pending.options.tone === "danger" ? "danger" : "default"}
          describe
          data-testid={testId}
          footer={
            <>
              {/* Cancel first, so Enter on an unmoved focus does not confirm a
                  destructive action. */}
              <Button
                onClick={() => {
                  close(false);
                }}
                data-testid={`${testId}-cancel`}
              >
                {pending.options.cancelLabel ?? "Cancel"}
              </Button>
              <Button
                variant="primary"
                tone={pending.options.tone === "danger" ? "danger" : "default"}
                onClick={() => {
                  close(true);
                }}
                data-testid={`${testId}-confirm`}
              >
                {pending.options.confirmLabel}
              </Button>
            </>
          }
        >
          {pending.options.body ?? null}
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}
